import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "@components/Button";
import { Card } from "@components/Card";
import { CloudinaryImg } from "@components/CloudinaryImg";
import { Link, LinkAppearance } from "@components/Link";
import { MarkdownText } from "@components/MarkdownText";
import { UpdatePostVideoPreview } from "@components/UpdatePosts/UpdatePostVideoPreview";
import { LikeButtonWithCounter } from "@components/feed/LikeButton";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useGateValue } from "@statsig/react-bindings";
import { useState } from "react";

import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import { PROFILE_IMG_PLACEHOLDER_ID } from "@every.org/common/src/entity/constants";
import { LikeableType } from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
} from "@every.org/common/src/helpers/clientRoutes";
import { FundraiserUpdatePost } from "@every.org/common/src/routes/fundraiserUpdatePost";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { truncatedTextCss } from "src/styles/truncatedTextCss";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { textSizeCss, FontWeight } from "src/theme/text";
import { ClickAction } from "src/utility/analytics";

interface UpdatePostViewProps {
  className?: string;
  variant?: UpdatePostViewVariant;
  updatePost: FundraiserUpdatePost;
  fundraiser: FundraiserResponse;
  nonprofit: ContextNonprofit;
}

enum UpdatePostViewVariant {
  A = "A",
  B = "B",
  C = "C",
  D = "D",
}

function getUpdatePostViewVariants(
  updatePosts: FundraiserUpdatePost[]
): UpdatePostViewVariant[] {
  if (updatePosts.length === 1) {
    return [UpdatePostViewVariant.A];
  } else if (updatePosts.length === 2) {
    return [UpdatePostViewVariant.A, UpdatePostViewVariant.B];
  }
  return [
    UpdatePostViewVariant.A,
    UpdatePostViewVariant.C,
    UpdatePostViewVariant.C,
  ];
}

const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1.91 / 1;
  overflow: hidden;
  border-radius: 8px;
`;

const updatePostCoverVideoCss = css`
  width: 100%;
  height: 100%;
`;

const updatePostViewCssBase = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.s};

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      flex-direction: column;
      ${ImageContainer} {
        max-width: unset;
        flex-shrink: 0;
      }
    `,
  })}
`;

const updatePostViewCssA = css`
  ${updatePostViewCssBase};
  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: {
      gap: spacing.l,
    },
  })};
`;

const updatePostViewCssC = css`
  ${updatePostViewCssBase}
`;

const updatePostViewCssD = css`
  ${updatePostViewCssBase}

  gap: ${spacing.s};

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      gap: ${spacing.s};
      flex-direction: row;
      ${ImageContainer} {
        max-width: 190px;
        flex-shrink: 0;
      }
    `,
  })}
`;

const updatePostViewCssB = css`
  ${updatePostViewCssBase};
  ${updatePostViewCssD};
  gap: ${spacing.s};

  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      gap: ${spacing.s};
      flex-direction: row;
      align-items: center;
      ${ImageContainer} {
        max-width: 300px;
        flex-shrink: 0;
      }
    `,
  })}
`;

const UpdatePostGrid = styled.div<{ numUpdatePosts: number }>`
  display: grid;
  gap: calc(${spacing.l} * 2);

  ${cssForMediaSize({
    max: MediaSize.MEDIUM_SMALL,
    css: css`
      & > :not(:last-of-type) {
        position: relative;

        &::after {
          content: "";
          position: absolute;
          right: 0;
          bottom: calc(-1 * ${spacing.l});
          left: 0;
          border-bottom: 1px solid var(${colorCssVars.dividerSoft});
        }
      }
    `,
  })};

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      grid-template-columns: minmax(0, 1fr);
      gap: calc(${spacing.l} * 2);
    `,
  })};

  ${({ numUpdatePosts }) =>
    numUpdatePosts >= 2 &&
    cssForMediaSize({
      min: MediaSize.MEDIUM,
      css: css`
        & > :first-child {
          position: relative;

          &::after {
            content: "";
            position: absolute;
            right: 0;
            bottom: calc(-1 * ${spacing.l});
            left: 0;
            border-bottom: 1px solid var(${colorCssVars.dividerSoft});
          }
        }
      `,
    })}

  ${({ numUpdatePosts }) =>
    numUpdatePosts === 3 &&
    cssForMediaSize({
      min: MediaSize.MEDIUM,
      css: css`
        grid-template-columns: repeat(2, minmax(0, 1fr));

        & > :first-child {
          grid-column: 1 / -1;
        }

        & > :nth-child(2) {
          position: relative;

          &::after {
            content: "";
            position: absolute;
            top: 0;
            right: calc(-1 * ${spacing.l});
            bottom: 0;
            border-right: 1px solid var(${colorCssVars.dividerSoft});
          }
        }
      `,
    })}
`;

const UpdatePostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: calc(${spacing.l} * 2);
  position: relative;
  margin-top: ${spacing.l};

  &::before {
    content: "";
    position: absolute;
    right: 0;
    top: calc(-1 * ${spacing.l});
    left: 0;
    border-bottom: 1px solid var(${colorCssVars.dividerSoft});
  }

  & > :not(:last-of-type) {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      right: 0;
      bottom: calc(-1 * ${spacing.l});
      left: 0;
      border-bottom: 1px solid var(${colorCssVars.dividerSoft});
    }
  }
`;

const updatePostViewCssMap: Record<UpdatePostViewVariant, any> = {
  [UpdatePostViewVariant.A]: updatePostViewCssA,
  [UpdatePostViewVariant.B]: updatePostViewCssB,
  [UpdatePostViewVariant.C]: updatePostViewCssC,
  [UpdatePostViewVariant.D]: updatePostViewCssD,
};

export const UpdatePostView = (props: UpdatePostViewProps) => {
  const coverageImageId =
    props.updatePost.coverImageCloudinaryId ||
    props.fundraiser.coverImageCloudinaryId ||
    props.nonprofit.coverImageCloudinaryId ||
    PROFILE_IMG_PLACEHOLDER_ID;
  const updatePostPath = getRoutePath({
    name: ClientRouteName.FUNDRAISER_UPDATE_VIEW,
    tokens: {
      fundraiserSlug: props.fundraiser.slug,
      nonprofitSlug: props.nonprofit.primarySlug,
      postShortId: String(props.updatePost.shortId),
    },
  });

  return (
    <div
      css={[
        updatePostViewCssMap[props.variant || UpdatePostViewVariant.A],
        { position: "relative" },
      ]}
    >
      <Link
        css={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
        }}
        appearance={LinkAppearance.UNSTYLED}
        aria-label={`View update: ${props.updatePost.title}`}
        data-tname="fundraiser-update-post"
        to={updatePostPath}
      />
      <ImageContainer>
        {props.updatePost.coverVideoUrl ? (
          <UpdatePostVideoPreview
            videoUrl={props.updatePost.coverVideoUrl}
            containerCss={updatePostCoverVideoCss}
            postUrl={updatePostPath}
            title={props.updatePost.title}
          />
        ) : (
          <CloudinaryImg
            cloudinaryId={coverageImageId}
            layout="fill"
            sizes="100vw"
          />
        )}
      </ImageContainer>

      <div css={[verticalStackCss.xs, { width: "100%" }]}>
        <h5>{props.updatePost.title}</h5>
        {props.updatePost.content && (
          <div
            css={truncatedTextCss({
              numLines: 3,
            })}
          >
            <MarkdownText content={props.updatePost.content} hideMedia />
          </div>
        )}
        <div
          css={[
            textSizeCss.xs,
            {
              color: `var(${colorCssVars.text.secondary})`,
            },
          ]}
        >
          Fundraiser:{" "}
          <span
            css={{
              fontWeight: FontWeight.MEDIUM,
            }}
          >
            {props.fundraiser.title}
          </span>
        </div>
        <div
          css={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <LikeButtonWithCounter
            aria-label={`Like ${props.updatePost.title}`}
            data-tname="update-post-like-button"
            data-action={ClickAction.LIKE}
            role={ButtonRole.TEXT_ONLY}
            size={ButtonSize.SMALL}
            css={{ position: "relative", zIndex: 2 }}
            type={LikeableType.UPDATE_POST}
            id={props.updatePost.id}
            loggedInUserLikes={props.updatePost.likesInfo.hasLoggedInUserLiked}
            likeCount={props.updatePost.likesInfo.count}
            updatePostLikeRouteTokens={{
              fundraiserIdentifier: props.fundraiser.id,
              nonprofitIdentifier: props.nonprofit.id,
            }}
          />
          {props.updatePost.publishedAt && (
            <time
              css={{ color: `var(${colorCssVars.text.secondary})` }}
              dateTime={props.updatePost.publishedAt.toISOString()}
            >
              {props.updatePost.publishedAt.toLocaleDateString()}
            </time>
          )}
        </div>
      </div>
    </div>
  );
};

interface FundraiserPageUpdatePostsListProps {
  updatePosts: FundraiserUpdatePost[];
  fundraiser: FundraiserResponse;
  nonprofit: ContextNonprofit;
}

export const FundraiserPageUpdatePostsList = ({
  updatePosts,
  fundraiser,
  nonprofit,
}: FundraiserPageUpdatePostsListProps) => {
  const [showAllUpdatePosts, setShowAllUpdatePosts] = useState(false);
  const showFundraiserUpdatePosts = useGateValue(
    "show_fundraiser_update_posts"
  );

  if (!showFundraiserUpdatePosts || updatePosts.length === 0) {
    return null;
  }

  const firstThreeUpdatePosts = updatePosts.slice(0, 3);
  const remainingUpdatePosts = updatePosts.slice(3);
  const updatePostViewVariants = getUpdatePostViewVariants(
    firstThreeUpdatePosts
  );

  return (
    <Card css={verticalStackCss.m}>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
        `}
      >
        <h4>Updates</h4>
        {!showAllUpdatePosts && remainingUpdatePosts.length > 0 && (
          <Button
            data-tname="show-all-update-posts-button"
            role={ButtonRole.TEXT_ONLY}
            size={ButtonSize.SMALL}
            onClick={{
              kind: ButtonTargetKind.FUNCTION,
              action: () => setShowAllUpdatePosts((prevState) => !prevState),
            }}
          >
            See all updates
          </Button>
        )}
      </div>
      <UpdatePostGrid numUpdatePosts={firstThreeUpdatePosts.length}>
        {firstThreeUpdatePosts.map((updatePost, i) => (
          <UpdatePostView
            key={updatePost.id}
            updatePost={updatePost}
            fundraiser={fundraiser}
            nonprofit={nonprofit}
            variant={updatePostViewVariants[i]}
          />
        ))}
      </UpdatePostGrid>
      {showAllUpdatePosts && remainingUpdatePosts.length > 0 && (
        <UpdatePostList>
          {remainingUpdatePosts.map((updatePost) => (
            <UpdatePostView
              key={updatePost.id}
              updatePost={updatePost}
              fundraiser={fundraiser}
              nonprofit={nonprofit}
              variant={UpdatePostViewVariant.D}
            />
          ))}
        </UpdatePostList>
      )}
    </Card>
  );
};
